/**
 * Conciliación del histórico.
 *
 * Acá se resuelve lo que el Excel dejó ambiguo. La regla de la reconstrucción:
 * se cargó **todo lo que tiene evidencia**, cada fila declara de dónde salió, y
 * lo que no tiene respaldo entró como borrador para revisión — nunca como una
 * cuenta por cobrar. Esta pantalla es donde esa cola se vacía.
 */

import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  dateLabel, periodLabel, PROVENANCE_HINT, PROVENANCE_LABEL, PROVENANCE_TONE, type Provenance,
} from '@cleanblue/shared';
import { api } from '@/lib/api';
import { useApi, useApiMutation } from '@/lib/hooks';
import { haptic } from '@/lib/haptics'; // HAPTIC_FEEDBACK
import {
  Async, Button, Card, Cell, Empty, Field, Modal, Money, Note, PageHead, StatusBadge, Table,
} from '@/components/ui';
import '@/styles/cobros.css';

export interface ReviewCharge {
  id: string; unit_code: string; unit_name: string | null; period_start: string;
  service_name_snapshot: string; total_minor: number; provenance: Provenance;
  provenance_note: string | null; status: string;
}

export function ReconciliationPage() {
  const query = useApi<{ charges: ReviewCharge[] }>('/admin/charges?needs_review=1');
  const [deciding, setDeciding] = useState<ReviewCharge | null>(null);

  return (
    <>
      <PageHead
        title="Conciliación"
        subtitle="Los meses del histórico que el Excel no dejó claros. Nada de esto se cobra hasta que alguien decida."
      />

      <Note tone="info">
        El color de celda del Excel guardaba significado que el valor no guardaba. Verde con «–»
        significaba que el consultorio no estaba; una celda en blanco significa <strong>no se sabe</strong>,
        y no se asumió nada. Es una de las razones de peso para sacar esto de Excel.
      </Note>

      <Async query={query} empty={(d) => d.charges.length === 0 && (
        <Empty
          icon="confirmar"
          title="No queda nada por conciliar"
          hint="Todo el histórico está resuelto."
          action={<Link to="/admin/cobros" className="btn">Ver los cobros</Link>}
        />
      )}>
        {(data) => (
          <Card flush title={`${data.charges.length} meses esperando una decisión`}>
            <Table head={
              /* Consultorio y Período juntos identifican la fila (puede haber
                 varios meses pendientes del mismo consultorio); Monto es lo
                 otro que se mira antes de decidir. La procedencia
                 («Evidencia») se ve completa al abrir Resolver, que es donde
                 se decide de verdad — por eso es la que se oculta. */
              <tr>
                <th>Consultorio</th><th>Período</th><th className="right">Monto</th>
                <th className="col-hide-sm">Evidencia</th><th />
              </tr>
            }>
              {data.charges.map((c) => (
                <tr key={c.id}>
                  <td className="strong">
                    <Cell sub={c.unit_name}>{c.unit_code}</Cell>
                  </td>
                  <td>{periodLabel(c.period_start)}</td>
                  <td className="right"><Money value={c.total_minor} dim /></td>
                  <td className="col-hide-sm">
                    <StatusBadge
                      value={c.provenance}
                      labels={PROVENANCE_LABEL}
                      tones={PROVENANCE_TONE}
                      hints={PROVENANCE_HINT}
                    />
                    {c.provenance_note && <span className="tbl__sub">{c.provenance_note}</span>}
                  </td>
                  <td className="right">
                    <Button variant="primary" onClick={() => setDeciding(c)}>Resolver</Button>
                  </td>
                </tr>
              ))}
            </Table>
          </Card>
        )}
      </Async>

      {deciding && <DecisionModal charge={deciding} onClose={() => setDeciding(null)} />}
    </>
  );
}

export function DecisionModal({ charge, onClose }: { charge: ReviewCharge; onClose: () => void }) {
  const [resolution, setResolution] = useState<'CONFIRM' | 'VOID'>('CONFIRM');
  const [note, setNote] = useState('');

  const mutation = useApiMutation(
    () => api.post(`/admin/charges/${charge.id}/review`, { resolution, note: note || undefined }),
    ['/admin/charges', '/admin/dashboard', '/admin/matrix'],
    { onSuccess: () => { if (resolution === 'VOID') haptic.warning(); else haptic.success(); onClose(); } }, // HAPTIC_FEEDBACK
  );

  return (
    <Modal
      title={`${charge.unit_code} · ${periodLabel(charge.period_start)}`}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="primary" disabled={mutation.isPending} onClick={() => mutation.mutate()}>
            {mutation.isPending ? 'Guardando…' : 'Guardar decisión'}
          </Button>
        </>
      }
    >
      {mutation.error && <Note tone="danger">{mutation.error.message}</Note>}

      <p className="small muted mb-2">
        {PROVENANCE_HINT[charge.provenance]}
      </p>

      {/* La evidencia va ANTES de la decisión: es con lo que se decide, y
          ponerla después invitaría a resolver primero y mirar después. */}
      <EvidencePanel chargeId={charge.id} />

      <div className="radios">
        <label className={`radiocard${resolution === 'CONFIRM' ? ' is-on' : ''}`}>
          <input type="radio" checked={resolution === 'CONFIRM'} onChange={() => setResolution('CONFIRM')} />
          <span>
            <span className="radiocard__t">Sí se prestó el servicio</span>
            <span className="radiocard__d">
              El cobro queda confirmado y sale de esta cola. Se puede emitir y cobrar.
            </span>
          </span>
        </label>

        <label className={`radiocard${resolution === 'VOID' ? ' is-on' : ''}`}>
          <input type="radio" checked={resolution === 'VOID'} onChange={() => setResolution('VOID')} />
          <span>
            <span className="radiocard__t">No corresponde</span>
            <span className="radiocard__d">
              El consultorio no estaba, o el mes no se trabajó. Se anula y queda el registro de por qué.
            </span>
          </span>
        </label>
      </div>

      <div className="mt-3">
        <Field label="Nota" hint="Qué se averiguó y con quién. Queda en la bitácora.">
          <textarea className="input" rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
      </div>
    </Modal>
  );
}

/* ── Evidencia del expediente (CB-18) ─────────────────────────────────────────
 *
 * De los 50 cobros de esta cola, 40 no tienen **ningún** documento: 0 con
 * factura enlazada, 10 con pago. Sin poder adjuntar, esos 40 se deciden a
 * ciegas sobre plata real, y la evidencia que aparece después —la captura del
 * chat, el PDF del banco— no tiene dónde quedar guardada.
 *
 * Esto **no** es el panel de expediente completo de CB-10c: acá solo se
 * adjunta y se ve lo adjunto. El veredicto por caso y la jerarquía de
 * evidencia A–G son de ese otro paquete.
 *
 * Se carga al abrir el modal, no al pintar la lista: la cola de 50 no puede
 * volverse lenta por esto.
 */

interface EvidenceDoc {
  id: string; kind: string; filename: string | null; mime: string | null;
  size_bytes: number | null; created_at: string; uploaded_by_name: string | null;
}

/**
 * Qué es cada documento, en español.
 *
 * Vive acá y no en `packages/shared/labels.ts` porque solo lo usa esta
 * pantalla; si un segundo lugar necesita los mismos rótulos, ahí sí conviene
 * subirlos.
 */
const DOC_KIND_LABEL: Record<string, string> = {
  PAYMENT_RECEIPT: 'Comprobante de pago',
  INVOICE_PDF: 'Factura (PDF)',
  INVOICE_XML: 'Factura (XML)',
  SERVICE_EVIDENCE: 'Evidencia del servicio',
  CONTRACT: 'Contrato',
  ID: 'Identificación',
  OTHER: 'Otro documento',
};

/** Lo que se ofrece subir desde acá. `CONTRACT` e `ID` no son evidencia de un mes. */
const UPLOAD_KINDS = ['PAYMENT_RECEIPT', 'INVOICE_PDF', 'INVOICE_XML', 'SERVICE_EVIDENCE', 'OTHER'];

function fileSize(bytes: number | null): string {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function EvidencePanel({ chargeId }: { chargeId: string }) {
  const path = `/admin/charges/${chargeId}/documents`;
  const query = useApi<{ documents: EvidenceDoc[] }>(path);
  const [kind, setKind] = useState('PAYMENT_RECEIPT');
  const inputRef = useRef<HTMLInputElement>(null);

  const upload = useApiMutation<unknown, File>(
    (file) => {
      const form = new FormData();
      form.set('file', file);
      form.set('kind', kind);
      return api.post(path, form);
    },
    [path],
    { onSuccess: () => haptic.success() }, // HAPTIC_FEEDBACK
  );

  return (
    <div className="mb-3">
      <div className="msec__title">Evidencia</div>

      <Async query={query}>
        {(data) => (
          data.documents.length === 0 ? (
            // No se dice «sin evidencia» a secas: que no haya documento es
            // información para quien decide, no un estado vacío que consolar.
            <p className="small muted" style={{ margin: '4px 0 10px' }}>
              Este mes no tiene ningún documento adjunto. Si conseguís uno —una captura del chat,
              el comprobante del banco— subilo antes de resolver.
            </p>
          ) : (
            <div style={{ margin: '6px 0 10px' }}>
              {data.documents.map((d) => (
                <div key={d.id} className="evidence__item">
                  <span className="evidence__name">
                    {d.filename ?? 'Documento'}
                    <span className="tbl__sub">
                      {DOC_KIND_LABEL[d.kind] ?? d.kind} · {dateLabel(d.created_at.slice(0, 10))}
                      {d.uploaded_by_name && ` · ${d.uploaded_by_name}`}
                    </span>
                  </span>
                  <span className="evidence__meta">{fileSize(d.size_bytes)}</span>
                  {/* El archivo vive en un bucket privado: este enlace va al
                      Worker, que comprueba la sesión antes de entregarlo. */}
                  <a
                    className="btn btn--sm"
                    href={`/api/admin/documents/${d.id}/file`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Abrir
                  </a>
                </div>
              ))}
            </div>
          )
        )}
      </Async>

      {upload.error && <Note tone="danger">{upload.error.message}</Note>}

      <div className="row row--wrap" style={{ gap: 6 }}>
        <select
          className="select"
          value={kind}
          onChange={(e) => setKind(e.target.value)}
          aria-label="Qué es el documento"
          style={{ maxWidth: 220 }}
        >
          {UPLOAD_KINDS.map((k) => (
            <option key={k} value={k}>{DOC_KIND_LABEL[k]}</option>
          ))}
        </select>

        <input
          ref={inputRef}
          type="file"
          accept="image/*,application/pdf,.xml,text/xml,application/xml"
          style={{ display: 'none' }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) upload.mutate(file);
            // Se limpia para que subir el mismo archivo dos veces vuelva a disparar el `change`.
            e.target.value = '';
          }}
        />
        <Button onClick={() => inputRef.current?.click()} disabled={upload.isPending}>
          {upload.isPending ? 'Subiendo…' : 'Adjuntar evidencia'}
        </Button>
        <span className="small muted">Imágenes, PDF o XML, hasta 8 MB.</span>
      </div>
    </div>
  );
}
